import React, { useMemo } from "react";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import { defineMessage, useIntl } from "react-intl";

import ActivityTypeWithCount from "@mapmycustomers/shared/types/entity/activities/ActivityTypeWithCount";

import NoChart from "../../charts/NoChart";

HighchartsMore(Highcharts);

const completedActivitiesChartTitle = defineMessage({
  id: "reports.overview.chart.completedActivities",
  defaultMessage: "Activities Done",
  description: "Done Activities chart title on the Overview page in Reports",
});

interface Props {
  activityTypesWithCount: ActivityTypeWithCount[];
}

const ActivityByTypeChart: React.FC<Props> = ({ activityTypesWithCount }) => {
  const intl = useIntl();
  const chartDataCompletedActivityTypesOverTime: Highcharts.Options = useMemo(() => {
    const sortedActivityTypesWithCount = activityTypesWithCount.sort((a, b) => b.count - a.count);
    return {
      chart: {
        height: 350,
        polar: true,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      pane: {
        size: "80%",
      },
      responsive: {
        rules: [
          {
            chartOptions: {
              legend: {
                align: "center",
                layout: "horizontal",
                verticalAlign: "bottom",
              },
              pane: {
                size: "70%",
              },
            },
            condition: {
              maxWidth: 500,
            },
          },
        ],
      },
      series: [
        {
          data: sortedActivityTypesWithCount.map(({ count }) => count),
          name: "",
          pointPlacement: "off",
          type: "line",
        },
      ],
      title: {
        text: intl.formatMessage(completedActivitiesChartTitle),
        x: -80,
      },
      tooltip: {
        pointFormat: '<span style="color:{series.color}"><b>{point.y}</b><br/>',
        shared: true,
      },
      xAxis: {
        categories: sortedActivityTypesWithCount.map(({ name }) => name),
        lineWidth: 0,
        tickmarkPlacement: "on",
      },
      yAxis: {
        gridLineInterpolation: "polygon",
        lineWidth: 0,
        min: 0,
      },
    };
  }, [activityTypesWithCount, intl]);

  return activityTypesWithCount.length > 0 ? (
    <HighchartsReact highcharts={Highcharts} options={chartDataCompletedActivityTypesOverTime} />
  ) : (
    <NoChart chartTitle={intl.formatMessage(completedActivitiesChartTitle)} />
  );
};

export default ActivityByTypeChart;
